import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { PageHeader, EmptyState } from '@/components/campus';
import {
  Shield,
  Calendar,
  Building2,
  DollarSign,
  MessageSquare,
  Lock,
  Loader2,
  Info,
  CheckCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tab configuration with icons
const TABS = [
  { key: 'events', label: 'Events', icon: Calendar },
  { key: 'org_management', label: 'Org Management', icon: Building2 },
  { key: 'finance', label: 'Finance', icon: DollarSign },
  { key: 'communications', label: 'Communications', icon: MessageSquare },
];

// Role display names
const ROLE_LABELS = {
  admin: 'Admin',
  event_manager: 'Event Manager',
  door_staff: 'Door Staff',
  member: 'Member',
};

// ==================== PERMISSION ROW COMPONENT ====================
const PermissionRow = ({ feature, roles, onToggle, savingKey }) => {
  return (
    <div className="flex items-center py-4 border-b border-white/[0.04] last:border-0">
      {/* Feature Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-cloud-white">{feature.label}</p>
        <p className="text-xs text-muted-text mt-0.5">{feature.description}</p>
      </div>

      {/* Role Toggles */}
      <div className="flex items-center gap-6 ml-6">
        {Object.entries(feature.roles).map(([role, config]) => {
          const isSaving = savingKey === `${feature.key}_${role}`;
          
          return (
            <div key={role} className="flex flex-col items-center w-24">
              {config.locked ? (
                <div className="relative group">
                  <div className={cn(
                    "w-10 h-6 rounded-full flex items-center justify-center",
                    config.enabled ? "bg-cool-mint/20" : "bg-white/[0.05]"
                  )}>
                    <Lock className={cn(
                      "w-3.5 h-3.5",
                      config.enabled ? "text-cool-mint" : "text-muted-text"
                    )} />
                  </div>
                  {/* Tooltip */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-obsidian rounded text-xs text-soft-silver whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                    {config.lock_reason}
                  </div>
                </div>
              ) : (
                <div className="relative">
                  <Switch
                    checked={config.enabled}
                    onCheckedChange={(checked) => onToggle(feature.key, role, checked)}
                    disabled={isSaving}
                    data-testid={`toggle-${feature.key}-${role}`}
                  />
                  {isSaving && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Loader2 className="w-4 h-4 text-electric-ice-blue animate-spin" />
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ==================== PERMISSION TABLE COMPONENT ====================
const PermissionTable = ({ features, roles, onToggle, savingKey }) => {
  return (
    <div className="bg-graphite/50 rounded-xl border border-white/[0.06] overflow-hidden">
      {/* Header */}
      <div className="flex items-center py-3 px-5 border-b border-white/[0.06] bg-white/[0.02]">
        <div className="flex-1">
          <span className="text-xs font-medium text-soft-silver uppercase tracking-wider">Feature</span>
        </div>
        <div className="flex items-center gap-6 ml-6">
          {roles.map((role) => (
            <div key={role} className="w-24 text-center">
              <span className="text-xs font-medium text-soft-silver uppercase tracking-wider">
                {ROLE_LABELS[role] || role}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Rows */}
      <div className="px-5">
        {features.map((feature) => (
          <PermissionRow
            key={feature.key}
            feature={feature}
            roles={roles}
            onToggle={onToggle}
            savingKey={savingKey}
          />
        ))}
      </div>
    </div>
  );
};

// ==================== MAIN PERMISSION SETTINGS PAGE ====================
export const PermissionSettings = () => {
  const { organization, session } = useAuth();
  const { hasOrgRole } = usePermissions();

  // Check permissions - owner only
  const isOwner = hasOrgRole(['owner']);

  // State
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [activeTab, setActiveTab] = useState('events');

  // Fetch permission config
  const fetchConfig = useCallback(async () => {
    if (!organization?.id || !session?.access_token) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(
        `${API_URL}/api/permissions/config/${organization.id}`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      if (res.ok) {
        const data = await res.json();
        setConfig(data);
      } else if (res.status === 403) {
        // Not owner - will show access denied
      } else {
        toast.error('Failed to load permission settings');
      }
    } catch (error) {
      console.error('Failed to fetch permission config:', error);
      toast.error('Failed to load permission settings');
    } finally {
      setLoading(false);
    }
  }, [organization?.id, session?.access_token]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Handle toggle
  const handleToggle = async (feature, role, enabled) => {
    const key = `${feature}_${role}`;
    setSavingKey(key);

    try {
      const res = await fetch(
        `${API_URL}/api/permissions/config/${organization.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ feature, role, enabled }),
        }
      );

      const data = await res.json();

      if (res.ok && data.success) {
        // Update local state
        setConfig((prev) => {
          const newConfig = { ...prev };
          Object.keys(newConfig.categories).forEach((cat) => {
            newConfig.categories[cat].features = newConfig.categories[cat].features.map((f) => {
              if (f.key === feature) {
                return {
                  ...f,
                  roles: {
                    ...f.roles,
                    [role]: { ...f.roles[role], enabled },
                  },
                };
              }
              return f;
            });
          });
          return newConfig;
        });
        toast.success('Permission updated');
      } else {
        toast.error(data.detail || 'Failed to update permission');
      }
    } catch (error) {
      console.error('Failed to update permission:', error);
      toast.error('Failed to update permission');
    } finally {
      setSavingKey(null);
    }
  };

  // Access denied
  if (!isOwner) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <EmptyState
          icon="lock"
          title="Access Restricted"
          description="Only organization owners can manage permission settings."
        />
      </div>
    );
  }

  // Loading
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-electric-ice-blue animate-spin" />
          <p className="text-soft-silver text-sm">Loading permission settings...</p>
        </div>
      </div>
    );
  }

  // No config loaded
  if (!config) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <EmptyState
          icon="alert-circle"
          title="Failed to Load"
          description="Could not load permission settings. Please try again."
          actionLabel="Retry"
          onAction={fetchConfig}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="permission-settings-page">
      <PageHeader
        title="Permission Settings"
        subtitle="Configure role-based access to features"
        icon={Shield}
      />

      {/* Info Banner */}
      <div className="flex items-start gap-3 p-4 bg-electric-ice-blue/5 rounded-lg border border-electric-ice-blue/20">
        <Info className="w-5 h-5 text-electric-ice-blue flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="text-cloud-white font-medium">How permissions work</p>
          <p className="text-soft-silver mt-1">
            Toggle permissions on/off for each role. Some permissions are locked:
            <span className="text-cool-mint"> Owner always has full access</span>, and
            <span className="text-signal-red"> Door Staff cannot access Dues or Documents</span>.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-graphite/50 border border-white/[0.06] p-1 rounded-lg">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <TabsTrigger
                key={tab.key}
                value={tab.key}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-md transition-colors",
                  "data-[state=active]:bg-electric-ice-blue/10 data-[state=active]:text-electric-ice-blue",
                  "data-[state=inactive]:text-soft-silver data-[state=inactive]:hover:text-cloud-white"
                )}
                data-testid={`tab-${tab.key}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* Tab Content */}
        {TABS.map((tab) => (
          <TabsContent key={tab.key} value={tab.key} className="mt-6">
            {config.categories[tab.key] && (
              <PermissionTable
                features={config.categories[tab.key].features}
                roles={config.roles}
                onToggle={handleToggle}
                savingKey={savingKey}
              />
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Legend */}
      <div className="flex items-center gap-6 pt-4 border-t border-white/[0.06]">
        <div className="flex items-center gap-2 text-xs text-soft-silver">
          <div className="w-8 h-4 rounded-full bg-cool-mint" />
          <span>Enabled</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-soft-silver">
          <div className="w-8 h-4 rounded-full bg-white/[0.1]" />
          <span>Disabled</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-soft-silver">
          <Lock className="w-4 h-4 text-muted-text" />
          <span>Locked (cannot change)</span>
        </div>
      </div>
    </div>
  );
};

export default PermissionSettings;
